// src/modules/Roles/components/modals/DeleteModal.tsx
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { PasswordInput } from '@shared/components/ui/PasswordInput';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { useUpdateProfile } from '../../hooks/useUpdateProfile';
import { useToast } from '@shared/hooks/use-toast';

export interface ConfirmPasswordModalRef {
  /**
   * Abre la modal de confirmación de contraseña.
   * @param newEmail El nuevo email a actualizar
   * @param onSuccess Callback que se ejecutará si el usuario acepta
   */
  open: (newEmail: string, onSuccess?: () => void) => void;
}

interface ConfirmPasswordModalProps {
  onCancel?: () => void;
}

export const ConfirmPasswordModal = forwardRef<ConfirmPasswordModalRef, ConfirmPasswordModalProps>((props, ref) => {
  const { t } = useTranslation('profileModule');
  const { updateEmail } = useUpdateProfile();
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const [password, setPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const onSuccessRef = useRef<(() => void) | undefined>();
  const skipEmailUpdateRef = useRef<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    open: (email: string, onSuccess?: () => void) => {
      setNewEmail(email);
      setPassword('');
      onSuccessRef.current = onSuccess;
      skipEmailUpdateRef.current = false; // Always false for now
      modalRef.current?.openModal();
    },
  }));

  const handleCancel = () => {
    modalRef.current?.closeModal();
    props.onCancel?.();
  };

  const handleConfirm = async () => {
    if (!password || !newEmail) return;
    setIsLoading(true);
    try {
      // Solo ejecutar updateEmail si no se debe saltar
      if (!skipEmailUpdateRef.current) {
        await updateEmail({ newEmail, password });
        toast({
          title: t('information.success_title'),
          description: t('information.success_description'),
        });
        toast({
          title: t('changeEmailModal.verificationTitle'),
          description: t('changeEmailModal.verificationInfo'),
          duration: 8000,
        });
      }

      modalRef.current?.closeModal();
      onSuccessRef.current?.();
    } catch (error: any) {
      let description = t('information.error_unknown');
      const title = t('information.error_title');
      if (
        error?.statusCode === 422 &&
        typeof error?.message === 'string' &&
        error.message.toLowerCase().includes('new email')
      ) {
        description = t('changeEmailModal.invalidEmailFormat');
      } else if (
        error?.response?.status === 422 ||
        error?.statusCode === 422 ||
        (typeof error?.message === 'string' && error.message.includes('422'))
      ) {
        description = t('changeEmailModal.incorrectPassword');
      } else if (error?.response?.status === 500 || error?.statusCode === 500) {
        description = t('changeEmailModal.invalidEmailFormat');
      } else if (error?.response?.data?.message) {
        description = error.response.data.message;
      } else if (error?.message) {
        description = error.message;
      }
      toast({
        title,
        description,
        variant: 'destructive',
      });
      modalRef.current?.closeModal();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalCenter ref={modalRef} title={t('changeEmailModal.title')} type="default" width="lg">
      <div className="space-y-4 mt-3">
        <Alert variant="default" className="text-sm">
          <div className="flex items-center gap-4">
            <FontAwesomeIcon icon={faCircleInfo} className="w-4 h-4" />
            <AlertDescription>{t('changeEmailModal.info')}</AlertDescription>
          </div>
        </Alert>
        <div>
          <label className="block text-sm mb-1 font-medium">{t('changeEmailModal.confirmPasswordLabel')}</label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('changeEmailModal.confirmPasswordPlaceholder')}
            className="w-full"
            data-testid="confirm-password-input"
            disabled={isLoading}
          />
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" onClick={handleCancel} disabled={isLoading}>
            {t('changeEmailModal.cancel')}
            <EscIcon className="w-4 h-4" />
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={!password || password.length < 7 || isLoading}
            loading={isLoading}
          >
            {t('changeEmailModal.confirm')}
            <EnterIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ConfirmPasswordModal.displayName = 'ConfirmPasswordModal';
